import type { AxiosProgressEvent } from 'axios';

export const UPLOAD_STATUS_DELAY_MS = 1000;

export interface UploadStatusTracker {
  onUploadProgress: (event: Pick<AxiosProgressEvent, 'loaded' | 'total'>) => void;
  /** True while part of the request body is still waiting to be sent. */
  isSending: () => boolean;
  getProgress: () => { loaded: number; total?: number };
  dispose: () => void;
}

/**
 * Decides when a run's execute request should show as "Uploading", from the request's upload progress events.
 * The delay starts at the first progress event, not when the request is made, because the request can wait
 * on a token refresh before any byte is sent. A body sent within the delay never shows, so fast runs look
 * like today. When the progress goes back to the start, the request is being sent again after a 401, and
 * the same rules apply to the new attempt.
 */
export function createUploadStatusTracker({
  onShow,
  onHide,
}: {
  onShow: () => void;
  onHide: () => void;
}): UploadStatusTracker {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let isShown = false;
  let isSending = false;
  let progress: { loaded: number; total?: number } = { loaded: 0 };

  const hide = () => {
    clearTimeout(timer);
    timer = undefined;
    if (isShown) {
      isShown = false;
      onHide();
    }
  };

  return {
    onUploadProgress: ({ loaded, total }) => {
      progress = { loaded, total };
      // Without a total we can't tell when the body is sent, so keep today's behavior.
      if (total === undefined || loaded >= total) {
        isSending = false;
        hide();
        return;
      }
      isSending = true;
      if (!isShown && timer === undefined) {
        timer = setTimeout(() => {
          timer = undefined;
          isShown = true;
          onShow();
        }, UPLOAD_STATUS_DELAY_MS);
      }
    },
    isSending: () => isSending,
    getProgress: () => progress,
    dispose: () => {
      isSending = false;
      hide();
    },
  };
}
